import React from 'react'
import {
  Avatar,
  Box,
  Button,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import {
  UnfoldMore as UnfoldMoreIcon,
  AccountCircle as AccountIcon,
  Logout as LogoutIcon,
  SmartToy as AgentsIcon,
  Palette as PaletteIcon,
  MenuBook as MenuBookIcon,
  Feedback as FeedbackIcon,
  OpenInNew as OpenInNewIcon,
  ArrowBack as ArrowBackIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { docsLinkUrl } from '../../docsLinks'
import { OrganizationPickerMenu } from '../ActiveOrganizationSwitcher'
import { VeilStreamSquareMark } from '../VeilStreamLogo'

export const SIDEBAR_WIDTH = 262
export const SIDEBAR_CONTENT_INSET = 24
export const SIDEBAR_TRANSITION_MS = 220

/** Square mark + wordmark lockup for the sidebar chrome (replaces the full logo image). */
export function SidebarBrand({ onHome }: { onHome?: () => void }) {
  const navigate = useNavigate()

  return (
    <Box
      component="a"
      href="/"
      aria-label="VeilStream home"
      onClick={(e) => {
        e.preventDefault()
        navigate('/')
        onHome?.()
      }}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        alignItems: 'center',
        gap: '8px',
        boxSizing: 'border-box',
        pl: '24px',
        pr: '12px',
        height: '48px',
        maxHeight: '48px',
        minHeight: '48px',
        overflow: 'hidden',
        mb: 0.25,
        textDecoration: 'none',
        color: 'var(--vs-text-primary)',
        borderRadius: 'var(--radius-sm)',
        '&:hover': { bgcolor: 'var(--vs-wash)' },
      }}
    >
      <VeilStreamSquareMark size={40} decorative />
      <Typography
        component="span"
        sx={{
          fontSize: '19px',
          fontWeight: 600,
          letterSpacing: '-0.03em',
          lineHeight: 1,
          color: 'inherit',
          whiteSpace: 'nowrap',
        }}
      >
        VeilStream
      </Typography>
    </Box>
  )
}

export function itemSx(active: boolean, py = 0.75): SxProps<Theme> {
  return {
    display: 'flex',
    alignItems: 'center',
    borderRadius: 'var(--radius-sm)',
    mb: 0.25,
    py,
    pl: `${SIDEBAR_CONTENT_INSET}px`,
    pr: 1.5,
    minHeight: 36,
    position: 'relative',
    color: active ? 'var(--vs-text-primary)' : 'var(--vs-text-muted)',
    bgcolor: active ? 'var(--vs-wash-strong)' : 'transparent',
    transition: `background-color var(--duration-fast) var(--ease-standard), color var(--duration-fast) var(--ease-standard)`,
    '&:hover': {
      bgcolor: active ? 'var(--vs-wash-strong)' : 'var(--vs-wash)',
      color: 'var(--vs-text-primary)',
    },
  }
}

export function iconSx(_active: boolean, minWidth = 28) {
  return {
    minWidth,
    mr: 1,
    mt: 0,
    mb: 0,
    color: 'inherit',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    '& .MuiSvgIcon-root': {
      fontSize: 20,
      display: 'block',
      color: 'inherit',
    },
  }
}

const listItemTextSx: SxProps<Theme> = {
  minWidth: 0,
  my: 0,
  '& .MuiListItemText-primary': {
    lineHeight: 1.2,
    display: 'block',
  },
}

export function SectionLabel({ label, tag }: { label: string; tag?: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        pl: `${SIDEBAR_CONTENT_INSET}px`,
        pr: 1.5,
        pt: 1.25,
        pb: 0.25,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: 'var(--vs-text-muted)',
          fontWeight: 'var(--type-overline-weight)',
          letterSpacing: 'var(--type-overline-spacing)',
          textTransform: 'uppercase',
          fontSize: 'var(--type-overline-size)',
          userSelect: 'none',
          lineHeight: 1,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {label}
      </Typography>
      {tag ? (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', lineHeight: 0 }}>
          {tag}
        </Box>
      ) : null}
    </Box>
  )
}

export function ShowMorePill({
  expanded,
  hiddenCount,
  onClick,
}: {
  expanded: boolean
  hiddenCount: number
  onClick: () => void
}) {
  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        ml: `${SIDEBAR_CONTENT_INSET}px`,
        mt: 0.25,
        mb: 0.5,
        px: 1.25,
        py: 0.3,
        borderRadius: 'var(--radius-pill)',
        border: '1px solid var(--vs-border-subtle)',
        cursor: 'pointer',
        color: 'var(--vs-text-muted)',
        fontSize: '0.72rem',
        userSelect: 'none',
        transition: `color var(--duration-fast) var(--ease-standard), border-color var(--duration-fast) var(--ease-standard)`,
        '&:hover': {
          color: 'var(--vs-text-secondary)',
          borderColor: 'var(--vs-border)',
        },
      }}
    >
      {expanded ? 'Show less' : `+ ${hiddenCount} more`}
    </Box>
  )
}

export function NavItem({
  text,
  icon,
  active,
  onClick,
  pl,
  fontSize = '0.875rem',
  badge,
  tag,
  title,
}: {
  text: string
  icon: React.ReactNode
  active: boolean
  onClick: () => void
  pl?: number
  fontSize?: string
  badge?: number | null
  /** Small label after the text (e.g. Beta). */
  tag?: React.ReactNode
  /** Full label for truncated text (defaults to `text`). */
  title?: string
}) {
  const decorated = badge != null || tag != null
  return (
    <ListItemButton
      disableGutters
      sx={{ ...itemSx(active), ...(pl != null ? { pl } : {}) }}
      onClick={onClick}
      title={title ?? text}
    >
      <ListItemIcon sx={iconSx(active)}>{icon}</ListItemIcon>
      <ListItemText
        sx={listItemTextSx}
        primary={
          decorated ? (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {text}
              </Box>
              {tag}
              {badge != null ? (
                <Box
                  component="span"
                  sx={{
                    minWidth: 18,
                    height: 18,
                    px: 0.5,
                    borderRadius: 'var(--radius-pill)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    flexShrink: 0,
                    bgcolor: badge > 0 ? 'var(--vs-warning)' : 'var(--vs-wash-strong)',
                    color: badge > 0 ? 'var(--vs-on-action)' : 'var(--vs-text-muted)',
                  }}
                >
                  {badge}
                </Box>
              ) : null}
            </Box>
          ) : (
            text
          )
        }
        primaryTypographyProps={{
          fontSize,
          fontWeight: active ? 600 : 400,
          noWrap: true,
          color: 'inherit',
        }}
      />
    </ListItemButton>
  )
}

export function ContextSidebarHeader({
  onExit,
  onHome,
  title,
  loading,
  onTitleClick,
  titleActive,
  visitHref,
}: {
  onExit: () => void
  onHome?: () => void
  title: string
  loading?: boolean
  onTitleClick?: () => void
  titleActive?: boolean
  visitHref?: string | null
}) {
  const displayTitle = loading ? '…' : title || '—'
  const visitButton = visitHref !== undefined && (
    <Tooltip title={visitHref ? 'Open preview URL' : 'Available when the environment is live'} arrow>
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', alignSelf: 'center', lineHeight: 0 }}>
        <Button
          size="small"
          variant="outlined"
          color="primary"
          component={visitHref ? 'a' : 'button'}
          href={visitHref || undefined}
          target={visitHref ? '_blank' : undefined}
          rel={visitHref ? 'noopener noreferrer' : undefined}
          disabled={!visitHref}
          aria-label="Visit environment"
          endIcon={<OpenInNewIcon />}
          onClick={(e) => e.stopPropagation()}
          sx={{
            flexShrink: 0,
            textTransform: 'none',
            py: 0,
            px: 0.75,
            minHeight: 24,
            height: 24,
            minWidth: 0,
            fontSize: '0.75rem',
            fontWeight: 600,
            lineHeight: 1,
            display: 'inline-flex',
            alignItems: 'center',
            '& .MuiButton-endIcon': {
              ml: 0.5,
              mr: 0,
              display: 'inline-flex',
              alignItems: 'center',
              '& > *:nth-of-type(1)': { fontSize: 14, display: 'block' },
            },
          }}
        >
          Visit
        </Button>
      </Box>
    </Tooltip>
  )

  return (
    <Box sx={{ pt: 0.25, pb: 1, borderBottom: '1px solid var(--vs-border-subtle)' }}>
      <SidebarBrand onHome={onHome} />
      <ListItemButton
        disableGutters
        onClick={onExit}
        sx={{
          display: 'flex',
          alignItems: 'center',
          borderRadius: 'var(--radius-sm)',
          py: 0.75,
          pl: `${SIDEBAR_CONTENT_INSET}px`,
          pr: 1.5,
          mb: 1,
          minHeight: 36,
          color: 'var(--vs-text-muted)',
          '&:hover': { bgcolor: 'var(--vs-wash)', color: 'var(--vs-text-primary)' },
        }}
      >
        <ListItemIcon sx={iconSx(false)}>
          <ArrowBackIcon />
        </ListItemIcon>
        <ListItemText
          primary="Back"
          sx={listItemTextSx}
          primaryTypographyProps={{ fontSize: '0.8125rem', fontWeight: 500, color: 'inherit' }}
        />
      </ListItemButton>
      {onTitleClick ? (
        <Box
          sx={{
            ...itemSx(Boolean(titleActive), 0.5),
            mb: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
          }}
        >
          <ListItemButton
            disableGutters
            onClick={onTitleClick}
            aria-current={titleActive ? 'page' : undefined}
            sx={{
              flex: 1,
              minWidth: 0,
              py: 0,
              px: 0,
              minHeight: 24,
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'transparent',
              '&:hover': { bgcolor: 'transparent' },
            }}
          >
            <Typography
              variant="subtitle1"
              noWrap
              title={loading ? undefined : title || undefined}
              sx={{
                fontWeight: 700,
                color: 'inherit',
                lineHeight: 1.2,
                display: 'block',
              }}
            >
              {displayTitle}
            </Typography>
          </ListItemButton>
          {visitButton}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            pl: `${SIDEBAR_CONTENT_INSET}px`,
            pr: 1.5,
            minHeight: 28,
          }}
        >
          <Typography
            variant="subtitle1"
            noWrap
            title={loading ? undefined : title || undefined}
            sx={{
              flex: 1,
              minWidth: 0,
              fontWeight: 700,
              color: 'var(--vs-text-primary)',
              lineHeight: 1.2,
            }}
          >
            {displayTitle}
          </Typography>
          {visitButton}
        </Box>
      )}
    </Box>
  )
}

export function UserSidebarFooter() {
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const [userMenuAnchor, setUserMenuAnchor] = React.useState<null | HTMLElement>(null)
  const [orgMenuAnchor, setOrgMenuAnchor] = React.useState<null | HTMLElement>(null)
  const profileButtonRef = React.useRef<HTMLDivElement | null>(null)

  if (!user) return null

  const handleUserMenuClose = () => setUserMenuAnchor(null)
  const handleLogout = () => {
    handleUserMenuClose()
    logout()
  }

  return (
    <Box sx={{ borderTop: '1px solid var(--vs-border-subtle)', pt: 1, pb: 0.5 }}>
      <ListItemButton
        ref={profileButtonRef}
        aria-label="user-profile"
        onClick={(e) => setUserMenuAnchor(e.currentTarget)}
        sx={{
          borderRadius: 1,
          py: 0.6,
          pl: `${SIDEBAR_CONTENT_INSET}px`,
          pr: 1.5,
          '&:hover': { bgcolor: 'var(--vs-wash)' },
          '&:focus-visible': { boxShadow: 'none', bgcolor: 'var(--vs-wash)' },
          '&.Mui-focusVisible': { bgcolor: 'var(--vs-wash)' },
        }}
      >
        {user.avatar_url ? (
          <Avatar
            src={user.avatar_url}
            alt={user.name || user.login}
            sx={{ width: 28, height: 28, flexShrink: 0, bgcolor: 'transparent' }}
          />
        ) : (
          <AccountIcon sx={{ fontSize: 28, color: 'var(--vs-text-secondary)', flexShrink: 0 }} />
        )}
        <Box sx={{ ml: 1.25, minWidth: 0, flex: 1 }}>
          <Typography variant="body2" noWrap sx={{ fontWeight: 500, color: 'var(--vs-text-primary)', lineHeight: 1.25 }}>
            {user.name || user.login}
          </Typography>
          {user.name && user.name !== user.login && (
            <Typography variant="caption" noWrap sx={{ color: 'var(--vs-text-muted)', display: 'block', lineHeight: 1.2 }}>
              {user.login}
            </Typography>
          )}
        </Box>
        <UnfoldMoreIcon sx={{ fontSize: 16, color: 'var(--vs-text-disabled)', flexShrink: 0, ml: 0.5 }} />
      </ListItemButton>
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{
          sx: {
            bgcolor: 'var(--vs-surface)',
            border: 'var(--border)',
            borderRadius: 'var(--radius-md)',
            minWidth: 176,
            boxShadow: 'var(--shadow-overlay)',
            mb: 0.5,
            '& .MuiMenuItem-root': {
              fontSize: '0.875rem',
              borderRadius: 'var(--radius-sm)',
              mx: 0.5,
              px: 1.25,
              py: 0.75,
              color: 'var(--vs-text-secondary)',
              '& .MuiListItemIcon-root': { minWidth: 32, color: 'var(--vs-text-muted)' },
              '&:hover': { bgcolor: 'rgba(134,190,223,0.10)', color: 'var(--vs-text-primary)' },
            },
            '& .MuiDivider-root': { borderColor: 'var(--vs-border-subtle)', mx: 1 },
          },
        }}
      >
        <Box sx={{ px: 1.75, pt: 1.25, pb: 1 }}>
          <Typography sx={{ fontSize: '0.75rem', color: 'var(--vs-text-muted)', letterSpacing: '0.02em' }}>
            {user.name || user.login}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={() => { handleUserMenuClose(); navigate('/settings/mcp-instructions') }}>
          <ListItemIcon><AgentsIcon sx={{ fontSize: 18 }} /></ListItemIcon>
          For agents
        </MenuItem>
        <MenuItem onClick={() => { handleUserMenuClose(); navigate('/settings') }}>
          <ListItemIcon><PaletteIcon sx={{ fontSize: 18 }} /></ListItemIcon>
          Account
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleUserMenuClose()
            setOrgMenuAnchor(profileButtonRef.current)
          }}
        >
          <ListItemIcon><GroupsIcon sx={{ fontSize: 18 }} /></ListItemIcon>
          Change organization
        </MenuItem>
        <MenuItem
          component="a"
          href={docsLinkUrl('home')}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleUserMenuClose}
          sx={{ color: 'var(--vs-text-primary) !important', textDecoration: 'none !important' }}
        >
          <ListItemIcon><MenuBookIcon sx={{ fontSize: 18 }} /></ListItemIcon>
          Docs
          <OpenInNewIcon sx={{ fontSize: 12, ml: 'auto', pl: 0.5, color: 'var(--vs-text-disabled)' }} />
        </MenuItem>
        <MenuItem onClick={() => { handleUserMenuClose(); navigate('/feedback') }}>
          <ListItemIcon><FeedbackIcon sx={{ fontSize: 18 }} /></ListItemIcon>
          Feedback
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon><LogoutIcon sx={{ fontSize: 18 }} /></ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
      <OrganizationPickerMenu
        anchorEl={orgMenuAnchor}
        open={Boolean(orgMenuAnchor)}
        onClose={() => setOrgMenuAnchor(null)}
      />
    </Box>
  )
}

export function SidebarPanel({
  children,
  visible,
  'data-testid': testId,
}: {
  children: React.ReactNode
  visible: boolean
  'data-testid'?: string
}) {
  return (
    <Box
      data-testid={testId}
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        width: visible ? SIDEBAR_WIDTH : 0,
        overflow: 'hidden',
        transition: (theme) =>
          theme.transitions.create('width', {
            duration: SIDEBAR_TRANSITION_MS,
            easing: theme.transitions.easing.easeOut,
          }),
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'none',
        },
      }}
    >
      <Box
        sx={{
          width: SIDEBAR_WIDTH,
          height: '100%',
          minHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'var(--vs-surface)',
          borderRight: '1px solid var(--vs-border-subtle)',
          transform: visible ? 'translateX(0)' : 'translateX(-100%)',
          transition: (theme) =>
            theme.transitions.create('transform', {
              duration: SIDEBAR_TRANSITION_MS,
              easing: theme.transitions.easing.easeOut,
            }),
          '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
            transform: 'none',
          },
        }}
      >
        {children}
      </Box>
    </Box>
  )
}

export function SidebarScrollList({ children }: { children: React.ReactNode }) {
  return (
    <List dense disablePadding sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', px: 0, pt: 0.75 }}>
      {children}
    </List>
  )
}
